<template>
    <section class="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div class="flex items-center gap-2 px-4 py-3" :class="{ 'border-b border-gray-200': !collapsed }">
            <button
                type="button"
                class="flex items-center gap-2 text-left"
                :aria-expanded="!collapsed"
                @click="collapsed = !collapsed"
            >
                <ChevronDownMiniSVG v-if="collapsed" fill="#25274D" iconClass="h-4 w-4" />
                <ChevronUpMiniSVG v-else fill="#25274D" iconClass="h-4 w-4" />
                <h2 class="font-semibold text-dark-primary">Published</h2>
                <span class="px-2 py-0.5 rounded-full bg-gray-100 text-xs text-gray-600">{{ publications.length }}</span>
            </button>
            <span class="text-xs text-gray-500">Snapshots of the appendix as it was sent to the client</span>
            <button
                v-if="canPublish && !showPublishForm && !viewingId"
                type="button"
                class="ml-auto h-8 px-3 rounded-md border border-dark-primary text-dark-primary text-sm font-medium hover:bg-dark-primary hover:text-white"
                @click="openPublishForm"
            >
                Publish…
            </button>
        </div>

        <template v-if="!collapsed">
            <div v-if="showPublishForm" class="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <p class="text-sm text-gray-700">
                    Saves the appendix as it is now:
                    <span class="font-medium">{{ countsText(currentCounts) }}</span>,
                    {{ columnLabels.length ? `with ${columnLabels.join(', ')}` : 'company names only' }}{{ groupByTier ? ', Exhibit A grouped by tier' : '' }}.
                </p>
                <label class="block mt-2 text-xs font-medium text-gray-600">
                    Note (optional)
                    <textarea
                        v-model="note"
                        rows="2"
                        class="mt-1 w-full rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-900 focus:border-accent-primary focus:outline-none focus:ring-1 focus:ring-accent-primary"
                        placeholder="e.g. Sent to the client with the October update"
                    ></textarea>
                </label>
                <div class="flex gap-2 mt-2">
                    <button
                        type="button"
                        class="h-8 px-3 rounded-md bg-dark-primary text-white text-sm font-medium hover:bg-accent-primary disabled:bg-gray-300"
                        :disabled="isPublishing"
                        @click="publish"
                    >
                        {{ isPublishing ? 'Publishing…' : 'Publish' }}
                    </button>
                    <button type="button" class="h-8 px-3 text-sm text-gray-600 hover:underline" @click="showPublishForm = false">Cancel</button>
                </div>
            </div>

            <div v-if="isLoading" class="px-4 py-4 text-sm text-gray-500">Loading…</div>
            <div v-else-if="!publications.length" class="px-4 py-4 text-sm text-gray-500">This appendix hasn't been published yet.</div>
            <ul v-else class="divide-y divide-gray-100">
                <li
                    v-for="publication in publications"
                    :key="publication.id"
                    class="px-4 py-2.5 text-sm"
                    :class="{ 'bg-amber-50': viewingId === publication.id }"
                >
                    <div class="flex flex-wrap items-center gap-x-4 gap-y-1">
                        <span class="font-medium text-gray-900 w-44">{{ formatDateTime(publication.published_at) }}</span>
                        <span class="text-gray-600 w-36 truncate">{{ userName(publication.published_by) }}</span>
                        <span class="text-gray-600 tabular-nums">{{ countsText(publication.counts) }}</span>
                        <span class="ml-auto flex items-center gap-3 text-xs">
                            <button v-if="viewingId === publication.id" type="button" class="font-medium text-amber-900 hover:underline" @click="emit('view', null)">Viewing · Back to current</button>
                            <button v-else type="button" class="font-medium text-accent-primary hover:underline disabled:text-gray-400" :disabled="busyId === publication.id" @click="view(publication)">View</button>
                            <button type="button" class="text-accent-primary hover:underline disabled:text-gray-400" :disabled="busyId === publication.id" @click="download(publication, 'docx')">Word</button>
                            <button type="button" class="text-accent-primary hover:underline disabled:text-gray-400" :disabled="busyId === publication.id" @click="download(publication, 'pdf')">PDF</button>
                            <button type="button" class="text-accent-primary hover:underline disabled:text-gray-400" :disabled="busyId === publication.id" @click="toggleChanges(publication)">
                                {{ comparingId === publication.id ? 'Hide changes' : 'Changes since' }}
                            </button>
                            <button v-if="canPublish" type="button" class="text-red-700 hover:underline disabled:text-gray-400" :disabled="busyId === publication.id" @click="remove(publication)">Delete</button>
                        </span>
                    </div>
                    <p v-if="publication.note" class="mt-0.5 text-xs text-gray-500 whitespace-pre-line">{{ publication.note }}</p>

                    <div v-if="comparingId === publication.id && changes" class="mt-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs">
                        <p v-if="!changes.added.length && !changes.moved.length && !changes.removed.length" class="text-gray-500">
                            No changes since this was published.
                        </p>
                        <template v-for="group in changeGroups" :key="group.key">
                            <div v-if="changes[group.key].length" class="mb-2 last:mb-0">
                                <p class="font-semibold text-gray-700">{{ group.label }} ({{ changes[group.key].length }})</p>
                                <ul class="mt-0.5 space-y-0.5">
                                    <li v-for="change in changes[group.key]" :key="change.company.id" class="text-gray-700">
                                        {{ change.company.company_name }}
                                        <span class="text-gray-500">{{ changeText(change) }}</span>
                                    </li>
                                </ul>
                            </div>
                        </template>
                    </div>
                </li>
            </ul>
        </template>
    </section>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits } from 'vue'
import { useStore } from 'vuex'
import { useToast } from 'vue-toast-notification'
import {
    getClientAppendixPublications, getClientAppendixPublication, createClientAppendixPublication,
    deleteClientAppendixPublication,
} from '@/services/Company.js'
import {
    CLIENT_APPENDICES, appendixChanges, exhibitName, downloadClientAppendixDocx, downloadClientAppendixPdf, formatFileDate,
} from '@/utils/clientAppendix'
import ChevronDownMiniSVG from '../svg-components/ChevronDownMiniSVG.vue'
import ChevronUpMiniSVG from '../svg-components/ChevronUpMiniSVG.vue'

// The client's published appendices: publish the current one, download a published one again, see what
// has changed in the appendix since, or delete a mistaken publish. The server takes the snapshot itself.
const props = defineProps({
    // The current appendix: { client, appendix_a, appendix_b, appendix_c }
    appendix: Object,
    columnLabels: Array,
    groupByTier: Boolean,
    // The publication shown in the page's exhibits, if any
    viewingId: Number,
    // The publication the page's exhibits are compared with, if any
    comparingId: Number,
})
// view: a publication's snapshot to show on the page, or null for the current appendix
// compare: a publication's snapshot to mark the current exhibits' changes against, or null to stop
const emit = defineEmits(['view', 'compare', 'deleted'])

const store = useStore()
const $toast = useToast()

// Same groups as CanMarkClientFeedback, which guards publishing and deleting
const canPublish = computed(() => {
    const permissions = store.state.auth.userPermissions ?? {}
    return Boolean(permissions.isDev || permissions.isAccountManager || permissions.isManagement
        || permissions.isSuperuser || permissions.isAccountStaff)
})

const collapsed = ref(false)
const isLoading = ref(false)
const publications = ref([])

const showPublishForm = ref(false)
const note = ref('')
const isPublishing = ref(false)

const busyId = ref(null)
// Snapshots are only fetched when downloaded or compared, then kept
const snapshots = new Map()

const currentCounts = computed(() => Object.fromEntries(
    CLIENT_APPENDICES.map(({ key }) => [key, props.appendix[key].length])))

const changes = computed(() => {
    const snapshot = snapshots.get(props.comparingId)
    return snapshot ? appendixChanges(snapshot.appendices, props.appendix) : null
})

const changeGroups = [
    { key: 'added', label: 'Added' },
    { key: 'moved', label: 'Moved' },
    { key: 'removed', label: 'No longer in the appendix' },
]

function changeText(change) {
    if (change.from && change.to) return `${exhibitName(change.from)} → ${exhibitName(change.to)}`
    return change.to ? `to ${exhibitName(change.to)}` : `was in ${exhibitName(change.from)}`
}

function countsText(counts) {
    return CLIENT_APPENDICES.map(({ key }) => `${exhibitName(key).replace('Exhibit ', '')}: ${counts[key] ?? 0}`).join(' · ')
}

function formatDateTime(dateString) {
    return new Date(dateString).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    })
}

function userName(user) {
    if (!user) return ''
    return [user.first_name, user.last_name].filter(Boolean).join(' ')
}

// Each load is numbered so a slower response for a client that has since changed is dropped
let latestRequest = 0

async function loadPublications() {
    const request = ++latestRequest
    isLoading.value = true
    const result = await getClientAppendixPublications(props.appendix.client.id, store.state.auth.token)
    if (request !== latestRequest) return
    isLoading.value = false
    if (result.status !== 200) {
        $toast.open({ type: 'error', duration: 10000, message: 'Could not load the published appendices.' })
        return
    }
    publications.value = result.data
}

watch(() => props.appendix.client.id, () => {
    publications.value = []
    showPublishForm.value = false
    loadPublications()
}, { immediate: true })

function openPublishForm() {
    note.value = ''
    showPublishForm.value = true
}

async function publish() {
    isPublishing.value = true
    const result = await createClientAppendixPublication(
        props.appendix.client.id, note.value.trim(), props.columnLabels, props.groupByTier, store.state.auth.token)
    isPublishing.value = false
    if (result.status !== 201) {
        $toast.open({ type: 'error', duration: 10000, message: 'Could not publish the appendix. Try again.' })
        return
    }
    showPublishForm.value = false
    $toast.open({ type: 'success', message: 'Appendix published.' })
    loadPublications()
}

async function snapshot(publication) {
    if (!snapshots.has(publication.id)) {
        const result = await getClientAppendixPublication(publication.id, store.state.auth.token)
        if (result.status !== 200) throw new Error(`Could not load publication ${publication.id}`)
        snapshots.set(publication.id, result.data)
    }
    return snapshots.get(publication.id)
}

async function withPublication(publication, errorMessage, action) {
    busyId.value = publication.id
    try {
        await action(await snapshot(publication))
    } catch (error) {
        console.log(error)
        $toast.open({ type: 'error', duration: 10000, message: errorMessage })
    } finally {
        busyId.value = null
    }
}

function download(publication, format) {
    const build = format === 'pdf' ? downloadClientAppendixPdf : downloadClientAppendixDocx
    return withPublication(publication, 'Could not build the document.', (published) => build(
        { client: props.appendix.client, ...published.appendices },
        published.column_labels, published.group_by_tier, formatFileDate(published.published_at)))
}

function view(publication) {
    showPublishForm.value = false
    return withPublication(publication, 'Could not load the published appendix.', (published) => emit('view', published))
}

function toggleChanges(publication) {
    if (props.comparingId === publication.id) {
        emit('compare', null)
        return
    }
    return withPublication(publication, 'Could not load the published appendix.', (published) => {
        emit('compare', published)
    })
}

async function remove(publication) {
    if (!window.confirm(`Delete the appendix published ${formatDateTime(publication.published_at)}? This can't be undone.`)) return
    busyId.value = publication.id
    const result = await deleteClientAppendixPublication(publication.id, store.state.auth.token)
    busyId.value = null
    if (result.status !== 204) {
        $toast.open({ type: 'error', duration: 10000, message: 'Could not delete the published appendix.' })
        return
    }
    snapshots.delete(publication.id)
    publications.value = publications.value.filter(({ id }) => id !== publication.id)
    emit('deleted', publication.id)
}
</script>
